import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API } from '../contexts/AuthContext';
import { exportToCsv } from '../helpers/exportCsv';

const PAGE_SIZE = 15;

export default function Usuarios() {
  const [vinculados, setVinculados] = useState([]);
  const [perfis, setPerfis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [busca, setBusca] = useState('');
  const [perfilFiltro, setPerfilFiltro] = useState('');
  const [pagina, setPagina] = useState(1);

  // Vincular novo
  const [buscaBase, setBuscaBase] = useState('');
  const [resultadosBase, setResultadosBase] = useState([]);
  const [buscandoBase, setBuscandoBase] = useState(false);
  const [perfilParaVincular, setPerfilParaVincular] = useState('');

  const [canais, setCanais] = useState([]);
  const [filiais, setFiliais] = useState([]);
  const [grupos, setGrupos] = useState([]);
  // Edicao do usuario num modal unico (perfil + canal padrao + extras + flag).
  const [editUser, setEditUser] = useState(null);
  const [salvandoEdicao, setSalvandoEdicao] = useState(false);
  const [form, setForm] = useState({ perfil_id: '', canal_id: '', extras: [], assumir: false });
  const [extrasOpen, setExtrasOpen] = useState(false);

  // Criacao de usuario direto em dbo.Usuarios (base compartilhada dos portais).
  const [criarOpen, setCriarOpen] = useState(false);
  const [criarForm, setCriarForm] = useState({
    nome: '', senha: '', confirmarSenha: '', email: '', filial: '', setor: '', perfil_id: '',
  });
  const [criando, setCriando] = useState(false);

  // Edicao dos dados base (email/filial/setor) e redefinir senha do usuario ja vinculado.
  const [editarBaseOpen, setEditarBaseOpen] = useState(false);
  const [editBaseForm, setEditBaseForm] = useState({ nome: '', email: '', filial: '', setor: '', senha: '' });

  // Gerenciamento direto de usuarios da base (independente de vinculo).
  const [buscaBaseCrud, setBuscaBaseCrud] = useState('');
  const [resultadosBaseCrud, setResultadosBaseCrud] = useState([]);
  const [buscandoBaseCrud, setBuscandoBaseCrud] = useState(false);
  // Alteracao de um usuario da base (modal dedicado — nao mexe em perfil/canais).
  const [alterarBase, setAlterarBase] = useState(null);
  const [alterarBaseForm, setAlterarBaseForm] = useState({ nome: '', email: '', filial: '', setor: '', senha: '', active: true });
  const [salvandoAlterarBase, setSalvandoAlterarBase] = useState(false);

  // Filiais extras (migration 066): concede acesso do usuario a outras filiais
  // alem da primaria em dbo.Usuarios.filial. Modal separado — nao mistura com
  // "Alterar" para nao dar impressao de que troca a filial principal.
  const [filiaisExtrasAlvo, setFiliaisExtrasAlvo] = useState(null); // usuario alvo
  const [filiaisExtrasSel, setFiliaisExtrasSel] = useState([]);      // codigos selecionados
  const [carregandoFiliaisExtras, setCarregandoFiliaisExtras] = useState(false);
  const [salvandoFiliaisExtras, setSalvandoFiliaisExtras] = useState(false);

  const carregar = () => {
    setLoading(true);
    setErro('');
    Promise.all([
      axios.get(`${API}/usuarios`),
      axios.get(`${API}/perfis`),
      axios.get(`${API}/atendimento/canais`).catch(() => ({ data: { data: [] } })),
      axios.get(`${API}/usuarios/filiais`).catch(() => ({ data: { data: [] } })),
      axios.get(`${API}/usuarios/grupos`).catch(() => ({ data: { data: [] } })),
    ])
      .then(([u, p, c, f, g]) => {
        setVinculados(u.data?.data || []);
        setPerfis(p.data?.data || []);
        setCanais(c.data?.data || []);
        setFiliais(f.data?.data || []);
        setGrupos(g.data?.data || []);
      })
      .catch(() => setErro('Não foi possivel carregar os usuários vinculados.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { carregar(); }, []);

  const buscarNaBase = async () => {
    if (buscaBase.trim().length < 2) return;
    setBuscandoBase(true);
    try {
      const res = await axios.get(`${API}/usuarios/buscar`, { params: { nome: buscaBase.trim() } });
      setResultadosBase(res.data?.data || []);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro', text: err.response?.data?.message || 'Erro ao buscar na base.' });
    } finally {
      setBuscandoBase(false);
    }
  };

  const vincular = async (usuarioBase) => {
    if (!perfilParaVincular) {
      Swal.fire({ icon: 'warning', title: 'Selecione um perfil antes de vincular.' });
      return;
    }
    try {
      await axios.post(`${API}/usuarios/vincular`, {
        usuario_id: usuarioBase.id,
        perfil_id: Number(perfilParaVincular),
      });
      Swal.fire({ icon: 'success', title: 'Usuário vinculado', timer: 1500, showConfirmButton: false });
      setBuscaBase('');
      setResultadosBase([]);
      setPerfilParaVincular('');
      carregar();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro', text: err.response?.data?.message || 'Erro ao vincular usuário.' });
    }
  };

  const abrirEdicao = (v) => {
    setForm({
      perfil_id: v.perfil_id || '',
      canal_id: v.canal_id || '',
      extras: Array.isArray(v.canais_extra) ? v.canais_extra : [],
      assumir: !!v.assumir_outros_canais,
    });
    setEditBaseForm({
      nome: v.nome || '',
      email: v.email || '',
      filial: v.filial || '',
      setor: v.setor || '',
      senha: '',
    });
    // Abre o bloco de dados base por padrao — o botao chama-se "Alterar usuario"
    // e a expectativa e ver os campos editaveis (email/filial/grupo/senha) direto.
    setEditarBaseOpen(true);
    setExtrasOpen(false);
    setEditUser(v);
  };

  const buscarNaBaseCrud = async () => {
    if (buscaBaseCrud.trim().length < 2) return;
    setBuscandoBaseCrud(true);
    try {
      const res = await axios.get(`${API}/usuarios/base`, { params: { nome: buscaBaseCrud.trim() } });
      setResultadosBaseCrud(res.data?.data || []);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro', text: err.response?.data?.message || 'Erro ao buscar na base.' });
    } finally {
      setBuscandoBaseCrud(false);
    }
  };

  const abrirAlterarBase = (u) => {
    setAlterarBaseForm({
      nome: u.nome || '',
      email: u.email || '',
      filial: u.filial || '',
      setor: u.setor || '',
      senha: '',
      active: !!u.active,
    });
    setAlterarBase(u);
  };

  const salvarAlterarBase = async () => {
    if (!alterarBase) return;
    const payload = {
      email: alterarBaseForm.email || null,
      filial: alterarBaseForm.filial || null,
      active: !!alterarBaseForm.active,
    };
    const novoNome = String(alterarBaseForm.nome || '').trim().toUpperCase();
    if (novoNome && novoNome !== String(alterarBase.nome || '').toUpperCase()) {
      if (novoNome.length < 3 || novoNome.length > 20) {
        Swal.fire({ icon: 'warning', title: 'Login deve ter entre 3 e 20 caracteres.' });
        return;
      }
      if (!/^[A-Z0-9._-]+$/.test(novoNome)) {
        Swal.fire({ icon: 'warning', title: 'Login aceita apenas letras, numeros, ponto, hifen e underline.' });
        return;
      }
      payload.nome = novoNome;
    }
    if (alterarBaseForm.setor && alterarBaseForm.setor !== (alterarBase.setor || '')) {
      payload.setor = alterarBaseForm.setor;
    }
    if (alterarBaseForm.senha) {
      if (alterarBaseForm.senha.length < 4 || alterarBaseForm.senha.length > 10) {
        Swal.fire({ icon: 'warning', title: 'Senha precisa ter entre 4 e 10 caracteres.' });
        return;
      }
      payload.senha = alterarBaseForm.senha;
    }
    setSalvandoAlterarBase(true);
    try {
      await axios.put(`${API}/usuarios/base/${alterarBase.id}`, payload);
      Swal.fire({ icon: 'success', title: 'Usuario atualizado', timer: 1300, showConfirmButton: false });
      setAlterarBase(null);
      // Recarrega busca da base e a lista de vinculados (caso o usuario alterado seja um deles).
      if (buscaBaseCrud.trim().length >= 2) buscarNaBaseCrud();
      carregar();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro', text: err.response?.data?.message || 'Erro ao salvar.' });
    } finally {
      setSalvandoAlterarBase(false);
    }
  };

  const abrirFiliaisExtras = async (u) => {
    setFiliaisExtrasAlvo(u);
    setFiliaisExtrasSel(Array.isArray(u.filiais_extras) ? u.filiais_extras.map((f) => String(f).toUpperCase()) : []);
    // Se a listagem ja veio com filiais_extras, evita o roundtrip; senao busca do backend.
    if (!Array.isArray(u.filiais_extras)) {
      setCarregandoFiliaisExtras(true);
      try {
        const res = await axios.get(`${API}/usuarios/base/${u.id}/filiais-extras`);
        setFiliaisExtrasSel((res.data?.data || []).map((f) => String(f).toUpperCase()));
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Erro', text: err.response?.data?.message || 'Erro ao carregar filiais extras.' });
        setFiliaisExtrasAlvo(null);
      } finally {
        setCarregandoFiliaisExtras(false);
      }
    }
  };

  const toggleFilialExtra = (codigo) => {
    const cod = String(codigo).toUpperCase();
    setFiliaisExtrasSel((atuais) => (
      atuais.includes(cod) ? atuais.filter((x) => x !== cod) : [...atuais, cod]
    ));
  };

  const salvarFiliaisExtras = async () => {
    if (!filiaisExtrasAlvo) return;
    setSalvandoFiliaisExtras(true);
    try {
      await axios.put(`${API}/usuarios/base/${filiaisExtrasAlvo.id}/filiais-extras`, {
        filiais: filiaisExtrasSel,
      });
      Swal.fire({ icon: 'success', title: 'Filiais atualizadas', timer: 1300, showConfirmButton: false });
      setFiliaisExtrasAlvo(null);
      if (buscaBaseCrud.trim().length >= 2) buscarNaBaseCrud();
      carregar();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro', text: err.response?.data?.message || 'Erro ao salvar filiais extras.' });
    } finally {
      setSalvandoFiliaisExtras(false);
    }
  };

  const excluirBaseUsuario = async (u) => {
    const confirm = await Swal.fire({
      icon: 'warning',
      title: `Excluir ${u.nome}?`,
      html: `
        <div style="text-align: left; font-size: 0.9rem;">
          <p><strong>${u.nome}</strong> sera <strong>desativado</strong> em <code>dbo.Usuarios</code>
          (base compartilhada dos portais) e perdera acesso a <strong>todos</strong> os
          portais Centrao — nao so o Portal TI.</p>
          <p style="margin-top:8px; color: #b43737;">Esta acao pode ser revertida marcando "Ativo" no formulario de Alterar.</p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Excluir usuario',
      confirmButtonColor: '#b43737',
      cancelButtonText: 'Cancelar',
      focusCancel: true,
    });
    if (!confirm.isConfirmed) return;
    try {
      await axios.delete(`${API}/usuarios/base/${u.id}`);
      Swal.fire({ icon: 'success', title: 'Usuario excluido', timer: 1500, showConfirmButton: false });
      if (buscaBaseCrud.trim().length >= 2) buscarNaBaseCrud();
      carregar();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro', text: err.response?.data?.message || 'Erro ao excluir usuario.' });
    }
  };

  const excluirUsuario = async (v) => {
    const confirm = await Swal.fire({
      icon: 'warning',
      title: `Excluir ${v.nome}?`,
      html: `
        <div style="text-align: left; font-size: 0.9rem;">
          <p><strong>${v.nome}</strong> sera <strong>desativado</strong> em <code>dbo.Usuarios</code>
          (base compartilhada dos portais) e perdera acesso a <strong>todos</strong> os
          portais Centrao — nao so o Portal TI.</p>
          <p style="margin-top:8px; color: #b43737;">Esta acao pode ser revertida reativando o usuario pelo mesmo formulario (dados base &rarr; ativo).</p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Excluir usuario',
      confirmButtonColor: '#b43737',
      cancelButtonText: 'Cancelar',
      focusCancel: true,
    });
    if (!confirm.isConfirmed) return;
    try {
      await axios.delete(`${API}/usuarios/base/${v.id}`);
      Swal.fire({ icon: 'success', title: 'Usuario excluido', timer: 1500, showConfirmButton: false });
      carregar();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro', text: err.response?.data?.message || 'Erro ao excluir usuario.' });
    }
  };

  const abrirCriar = () => {
    setCriarForm({ nome: '', senha: '', confirmarSenha: '', email: '', filial: '', setor: '', perfil_id: '' });
    setCriarOpen(true);
  };

  const criarUsuarioBase = async () => {
    const nome = criarForm.nome.trim().toUpperCase();
    if (nome.length < 3) {
      Swal.fire({ icon: 'warning', title: 'Login precisa ter ao menos 3 caracteres.' });
      return;
    }
    if (!criarForm.senha || criarForm.senha.length < 4 || criarForm.senha.length > 10) {
      Swal.fire({ icon: 'warning', title: 'Senha precisa ter entre 4 e 10 caracteres.' });
      return;
    }
    if (criarForm.senha !== criarForm.confirmarSenha) {
      Swal.fire({ icon: 'warning', title: 'As senhas nao conferem.' });
      return;
    }
    if (criarForm.filial && criarForm.filial.length !== 2) {
      Swal.fire({ icon: 'warning', title: 'Filial deve ter 2 caracteres (ex.: 01).' });
      return;
    }
    setCriando(true);
    try {
      await axios.post(`${API}/usuarios/base`, {
        nome,
        senha: criarForm.senha,
        email: criarForm.email || null,
        filial: criarForm.filial || null,
        setor: criarForm.setor || null,
        perfil_id: criarForm.perfil_id ? Number(criarForm.perfil_id) : null,
      });
      Swal.fire({
        icon: 'success',
        title: criarForm.perfil_id ? 'Usuario criado e vinculado' : 'Usuario criado',
        text: criarForm.perfil_id ? 'Ja pode acessar o Portal TI.' : 'Vincule a um perfil para conceder acesso.',
        timer: 2000,
        showConfirmButton: false,
      });
      setCriarOpen(false);
      carregar();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro', text: err.response?.data?.message || 'Erro ao criar usuario.' });
    } finally {
      setCriando(false);
    }
  };

  const salvarDadosBase = async () => {
    if (!editUser) return;
    // email/filial vao sempre com valor atual do form (null limpa no backend).
    // setor so vai se mudou de fato (null nao limpa — COALESCE mantem no backend).
    // nome idem: so envia se mudou (e passa pelas mesmas regras de criarBase).
    const payload = {
      email: editBaseForm.email || null,
      filial: editBaseForm.filial || null,
    };
    const novoNome = String(editBaseForm.nome || '').trim().toUpperCase();
    if (novoNome && novoNome !== String(editUser.nome || '').toUpperCase()) {
      if (novoNome.length < 3 || novoNome.length > 20) {
        Swal.fire({ icon: 'warning', title: 'Login deve ter entre 3 e 20 caracteres.' });
        return;
      }
      if (!/^[A-Z0-9._-]+$/.test(novoNome)) {
        Swal.fire({ icon: 'warning', title: 'Login aceita apenas letras, numeros, ponto, hifen e underline.' });
        return;
      }
      payload.nome = novoNome;
    }
    if (editBaseForm.setor && editBaseForm.setor !== (editUser.setor || '')) {
      payload.setor = editBaseForm.setor;
    }
    if (editBaseForm.senha) payload.senha = editBaseForm.senha;

    const mudouEmailOuFilial =
      (payload.email || '') !== (editUser.email || '') ||
      (payload.filial || '') !== (editUser.filial || '');
    if (!mudouEmailOuFilial && !payload.setor && !payload.senha && !payload.nome) {
      Swal.fire({ icon: 'info', title: 'Nada para atualizar.' });
      return;
    }
    if (payload.senha && (payload.senha.length < 4 || payload.senha.length > 10)) {
      Swal.fire({ icon: 'warning', title: 'Senha precisa ter entre 4 e 10 caracteres.' });
      return;
    }
    if (payload.filial && payload.filial.length !== 2) {
      Swal.fire({ icon: 'warning', title: 'Filial deve ter 2 caracteres.' });
      return;
    }
    setSalvandoEdicao(true);
    try {
      await axios.put(`${API}/usuarios/base/${editUser.id}`, payload);
      Swal.fire({ icon: 'success', title: 'Dados atualizados', timer: 1300, showConfirmButton: false });
      setEditarBaseOpen(false);
      setEditBaseForm((f) => ({ ...f, senha: '' }));
      carregar();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro', text: err.response?.data?.message || 'Erro ao salvar dados.' });
    } finally {
      setSalvandoEdicao(false);
    }
  };

  // Troca o canal padrao garantindo que ele nao fique tambem como extra.
  const mudarPadrao = (val) => {
    const canalId = val ? Number(val) : null;
    setForm((f) => ({ ...f, canal_id: val, extras: f.extras.filter((x) => x !== canalId) }));
  };

  const toggleExtra = (id) => {
    setForm((f) => {
      const has = f.extras.includes(id);
      const extras = has ? f.extras.filter((x) => x !== id) : [...f.extras, id];
      return { ...f, extras, assumir: extras.length === 0 ? false : f.assumir };
    });
  };

  const salvarEdicao = async () => {
    if (!editUser) return;
    if (!form.perfil_id) {
      Swal.fire({ icon: 'warning', title: 'Selecione um perfil.' });
      return;
    }
    setSalvandoEdicao(true);
    try {
      if (Number(form.perfil_id) !== Number(editUser.perfil_id)) {
        await axios.put(`${API}/usuarios/${editUser.vinculo_id}/perfil`, { perfil_id: Number(form.perfil_id) });
      }
      const canalId = form.canal_id ? Number(form.canal_id) : null;
      const extras = (form.extras || []).map(Number).filter((id) => id && id !== canalId);
      await axios.put(`${API}/usuarios/${editUser.vinculo_id}/canais`, {
        canal_id: canalId,
        canais_extra: extras,
        assumir_outros_canais: extras.length > 0 ? !!form.assumir : false,
      });
      Swal.fire({ icon: 'success', title: 'Usuário atualizado', timer: 1300, showConfirmButton: false });
      setEditUser(null);
      carregar();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro', text: err.response?.data?.message || 'Erro ao salvar.' });
    } finally {
      setSalvandoEdicao(false);
    }
  };

  const desvincular = async (v) => {
    const ok = await Swal.fire({
      icon: 'warning',
      title: `Desvincular ${v.nome}?`,
      text: 'O usuario perdera o acesso ao Portal TI.',
      showCancelButton: true,
      confirmButtonText: 'Desvincular',
      confirmButtonColor: '#b43737',
    });
    if (!ok.isConfirmed) return;
    try {
      await axios.delete(`${API}/usuarios/${v.vinculo_id}`);
      Swal.fire({ icon: 'success', title: 'Desvinculado', timer: 1300, showConfirmButton: false });
      carregar();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro', text: err.response?.data?.message || 'Erro ao desvincular.' });
    }
  };

  const vinculadosFiltrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return vinculados.filter((v) => {
      if (perfilFiltro && String(v.perfil_id || '') !== String(perfilFiltro)) return false;
      if (!termo) return true;
      return [v.nome, v.email, v.perfil_nome].filter(Boolean).join(' ').toLowerCase().includes(termo);
    });
  }, [busca, perfilFiltro, vinculados]);

  useEffect(() => { setPagina(1); }, [busca, perfilFiltro]);

  const totalPaginas = Math.max(1, Math.ceil(vinculadosFiltrados.length / PAGE_SIZE));
  const paginaAtual = Math.min(pagina, totalPaginas);
  const inicioIdx = (paginaAtual - 1) * PAGE_SIZE;
  const vinculadosPagina = vinculadosFiltrados.slice(inicioIdx, inicioIdx + PAGE_SIZE);

  const handleExport = () => {
    const colunas = [
      { label: 'Nome', value: (v) => v.nome || '' },
      { label: 'Filial', value: (v) => v.filial || '' },
      { label: 'Email', value: (v) => v.email || '' },
      { label: 'Perfil', value: (v) => v.perfil_nome || '' },
    ];
    const hoje = new Date().toISOString().slice(0, 10);
    exportToCsv(`usuarios_portal_ti_${hoje}`, colunas, vinculadosFiltrados);
  };

  return (
    <div className="content-shell">
      <section className="page-header">
        <div>
          <span className="section-header__eyebrow">Administração</span>
          <h1>Usuários</h1>
          <p>Conceda acesso ao Portal TI buscando usuarios da base do Centrao e atribuindo um perfil.</p>
        </div>
      </section>

      <section className="content-card">
        <div className="section-header section-header--compact" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <span className="section-header__eyebrow">Vincular</span>
            <h2>Conceder acesso a um usuário da base</h2>
          </div>
          <button type="button" className="btn-portal btn-portal--primary" onClick={abrirCriar}>
            <i className="bi bi-person-plus" /> Novo usuário
          </button>
        </div>

        <div className="filter-card" style={{ marginBottom: 12 }}>
          <div className="filter-card__field filter-card__field--grow">
            <i className="bi bi-search" />
            <input
              type="text"
              placeholder="Buscar por nome na base do Centrao (ex: DAVYD)"
              value={buscaBase}
              onChange={(e) => setBuscaBase(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && buscarNaBase()}
            />
          </div>
          <div className="filter-card__field">
            <select value={perfilParaVincular} onChange={(e) => setPerfilParaVincular(e.target.value)}>
              <option value="">Selecione o perfil...</option>
              {perfis.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
            </select>
          </div>
          <button
            type="button"
            className="btn-portal btn-portal--primary"
            onClick={buscarNaBase}
            disabled={buscandoBase || buscaBase.trim().length < 2}
          >
            <i className="bi bi-search" /> {buscandoBase ? ' Buscando...' : ' Buscar'}
          </button>
        </div>

        {resultadosBase.length > 0 && (
          <div className="base-results">
            {resultadosBase.map((u) => (
              <div key={u.id} className="base-result">
                <div className="base-result__info">
                  <span className="cell-icon"><i className="bi bi-person" /></span>
                  <div>
                    <strong>{u.nome}</strong>
                    <small>{u.filial ? `Filial ${u.filial}` : 'Sem filial'}</small>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-portal btn-portal--outline"
                  onClick={() => vincular(u)}
                  disabled={!perfilParaVincular}
                >
                  <i className="bi bi-link-45deg" /> Vincular
                </button>
              </div>
            ))}
          </div>
        )}
        {resultadosBase.length === 0 && buscaBase.trim().length >= 2 && !buscandoBase && (
          <p className="content-card__hint">Nenhum usuário encontrado na base ou todos já estao vinculados.</p>
        )}
      </section>

      <section className="content-card">
        <div className="section-header section-header--compact">
          <div>
            <span className="section-header__eyebrow">Base compartilhada</span>
            <h2>Gerenciar usuários da base</h2>
            <p style={{ fontSize: '0.85rem', color: 'var(--portal-muted)', margin: '4px 0 0' }}>
              Busca em <code>dbo.Usuarios</code>. Inclui usuarios ativos, inativos e vinculados a outros portais. Aqui voce pode <strong>alterar</strong> dados e <strong>excluir</strong> (soft delete) — independente do vinculo com o Portal TI.
            </p>
          </div>
        </div>

        <div className="filter-card" style={{ marginBottom: 12 }}>
          <div className="filter-card__field filter-card__field--grow">
            <i className="bi bi-search" />
            <input
              type="text"
              placeholder="Buscar na base (nome — min 2 caracteres)"
              value={buscaBaseCrud}
              onChange={(e) => setBuscaBaseCrud(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && buscarNaBaseCrud()}
            />
          </div>
          <button
            type="button"
            className="btn-portal btn-portal--primary"
            onClick={buscarNaBaseCrud}
            disabled={buscandoBaseCrud || buscaBaseCrud.trim().length < 2}
          >
            <i className="bi bi-search" /> {buscandoBaseCrud ? ' Buscando...' : ' Buscar'}
          </button>
        </div>

        {resultadosBaseCrud.length > 0 && (
          <div className="table-shell">
            <table className="portal-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Filial</th>
                  <th>Email</th>
                  <th>Grupo</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {resultadosBaseCrud.map((u) => (
                  <tr key={u.id} style={{ opacity: u.active ? 1 : 0.55 }}>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <span className="cell-icon"><i className="bi bi-person" /></span>
                        <span className="cell-strong">{u.nome}</span>
                      </div>
                    </td>
                    <td className="cell-muted">
                      {u.filial || '-'}
                      {Array.isArray(u.filiais_extras) && u.filiais_extras.length > 0 && (
                        <span
                          title={`Filiais adicionais: ${u.filiais_extras.join(', ')}`}
                          style={{ marginLeft: 6, fontSize: '0.75rem', padding: '2px 6px', borderRadius: 999, background: 'rgba(42,138,156,0.15)', color: '#2A8A9C' }}
                        >
                          +{u.filiais_extras.length}
                        </span>
                      )}
                    </td>
                    <td className="cell-muted">{u.email || '-'}</td>
                    <td className="cell-muted">{u.setor || '-'}</td>
                    <td>
                      {u.active ? (
                        <span className="permissao-chip permissao-chip--on">
                          {u.vinculo_id ? `Ativo · ${u.perfil_nome || 'sem perfil'}` : 'Ativo'}
                        </span>
                      ) : (
                        <span className="permissao-chip" style={{ background: 'rgba(180,55,55,0.15)', color: '#b43737' }}>Inativo</span>
                      )}
                    </td>
                    <td className="cell-chevron">
                      <div className="d-flex gap-2 justify-content-end">
                        <button
                          type="button"
                          className="icon-action"
                          title="Alterar usuário"
                          onClick={() => abrirAlterarBase(u)}
                        >
                          <i className="bi bi-pencil" />
                        </button>
                        <button
                          type="button"
                          className="icon-action"
                          title="Filiais adicionais (libera acesso do usuário em outras filiais)"
                          onClick={() => abrirFiliaisExtras(u)}
                          disabled={!u.active}
                        >
                          <i className="bi bi-buildings" />
                        </button>
                        <button
                          type="button"
                          className="icon-action icon-action--danger"
                          title="Excluir usuário (desativa em todos os portais)"
                          onClick={() => excluirBaseUsuario(u)}
                          disabled={!u.active}
                        >
                          <i className="bi bi-trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {resultadosBaseCrud.length === 0 && buscaBaseCrud.trim().length >= 2 && !buscandoBaseCrud && (
          <p className="content-card__hint">Nenhum usuário encontrado com esse nome.</p>
        )}
      </section>

      <div className="filter-card">
        <div className="filter-card__field filter-card__field--grow">
          <i className="bi bi-search" />
          <input
            type="text"
            placeholder="Filtrar vinculados por nome, email ou perfil"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
        <div className="filter-card__field">
          <select value={perfilFiltro} onChange={(e) => setPerfilFiltro(e.target.value)}>
            <option value="">Todos perfis</option>
            {perfis.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
          </select>
        </div>
      </div>

      {erro ? <div className="feedback-banner feedback-banner--error">{erro}</div> : null}

      <div className="table-toolbar">
        <span className="table-toolbar__count">{vinculadosFiltrados.length} usuario(s) vinculado(s)</span>
        <button type="button" className="btn-portal btn-portal--outline" onClick={handleExport} disabled={!vinculadosFiltrados.length}>
          <i className="bi bi-file-earmark-excel" /> Exportar Excel
        </button>
      </div>

      <section className="content-card">
        {loading ? (
          <div className="content-card--centered">
            <div className="screen-loader__spinner" />
            <p>Carregando vínculos...</p>
          </div>
        ) : vinculadosPagina.length ? (
          <>
            <div className="table-shell">
              <table className="portal-table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Email</th>
                    <th>Perfil</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {vinculadosPagina.map((v) => (
                    <tr key={v.vinculo_id}>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <span className="cell-icon"><i className="bi bi-person" /></span>
                          <span className="cell-strong">{v.nome}</span>
                        </div>
                      </td>
                      <td className="cell-muted">{v.email || '-'}</td>
                      <td>
                        <span className="permissao-chip permissao-chip--on">{v.perfil_nome || 'sem perfil'}</span>
                      </td>
                      <td className="cell-chevron">
                        <div className="d-flex gap-2 justify-content-end">
                          <button
                            type="button"
                            className="icon-action"
                            title="Alterar usuário"
                            onClick={() => abrirEdicao(v)}
                          >
                            <i className="bi bi-pencil" />
                          </button>
                          <button
                            type="button"
                            className="icon-action"
                            title="Desvincular do Portal TI (mantem usuario na base)"
                            onClick={() => desvincular(v)}
                          >
                            <i className="bi bi-x-circle" />
                          </button>
                          <button
                            type="button"
                            className="icon-action icon-action--danger"
                            title="Excluir usuário (desativa em todos os portais)"
                            onClick={() => excluirUsuario(v)}
                          >
                            <i className="bi bi-trash" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="pagination">
              <button type="button" className="pagination__btn" onClick={() => setPagina((p) => Math.max(1, p - 1))} disabled={paginaAtual <= 1}>
                <i className="bi bi-chevron-left" /> Anterior
              </button>
              <span className="pagination__info">Página {paginaAtual} de {totalPaginas}</span>
              <button type="button" className="pagination__btn" onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))} disabled={paginaAtual >= totalPaginas}>
                Próxima <i className="bi bi-chevron-right" />
              </button>
            </div>
          </>
        ) : (
          <div className="empty-state">
            <strong>Nenhum usuário vinculado</strong>
            <span>Use a busca acima para conceder acesso a um usuário da base.</span>
          </div>
        )}
      </section>

      {filiaisExtrasAlvo && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1065, padding: 16 }}
          onClick={() => !salvandoFiliaisExtras && setFiliaisExtrasAlvo(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'var(--portal-surface)', borderRadius: 12, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.35)' }}
          >
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--portal-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong><i className="bi bi-buildings me-2" />Filiais adicionais — {filiaisExtrasAlvo.nome}</strong>
              <button type="button" className="btn-close" onClick={() => setFiliaisExtrasAlvo(null)} disabled={salvandoFiliaisExtras} />
            </div>

            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12, fontSize: '0.85rem' }}>
              <div style={{ color: 'var(--portal-muted)', fontSize: '0.8rem' }}>
                Filial primária: <strong>{filiaisExtrasAlvo.filial || '— nenhuma —'}</strong>. Marque abaixo <strong>outras filiais existentes</strong> nas quais este usuário também deve ter acesso.
              </div>

              {carregandoFiliaisExtras ? (
                <div style={{ padding: 20, textAlign: 'center', color: 'var(--portal-muted)' }}>Carregando...</div>
              ) : filiais.length === 0 ? (
                <div style={{ padding: 12, borderRadius: 8, background: 'rgba(180,55,55,0.1)', color: '#b43737' }}>
                  Nenhuma filial cadastrada em <code>dbo.FILIAIS</code>.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 320, overflowY: 'auto', border: '1px solid var(--portal-border)', borderRadius: 8, padding: 10, background: 'var(--portal-surface-soft)' }}>
                  {filiais
                    .filter((f) => String(f.codigo).toUpperCase() !== String(filiaisExtrasAlvo.filial || '').toUpperCase())
                    .map((f) => {
                      const cod = String(f.codigo).toUpperCase();
                      const marcado = filiaisExtrasSel.includes(cod);
                      return (
                        <label key={cod} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '4px 6px', borderRadius: 6 }}>
                          <input
                            type="checkbox"
                            style={{ accentColor: 'var(--portal-blue)' }}
                            checked={marcado}
                            onChange={() => toggleFilialExtra(cod)}
                          />
                          <i className="bi bi-building" style={{ color: '#2A8A9C', fontSize: '0.8rem' }} />
                          <span><strong>{cod}</strong> · {f.nome}</span>
                        </label>
                      );
                    })}
                </div>
              )}

              <small style={{ color: 'var(--portal-muted)' }}>
                A filial primária não aparece na lista — para trocá-la use "Alterar" no menu do usuário.
                As filiais extras vivem em <code>siti_usuario_filial_extra</code> (Portal TI) e ficam disponíveis no token de sessão como <code>filiais_extras</code>.
              </small>
            </div>

            <div style={{ padding: '14px 20px', borderTop: '1px solid var(--portal-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <small style={{ color: 'var(--portal-muted)' }}>
                {filiaisExtrasSel.length} filial(is) extra(s) selecionada(s)
              </small>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="btn-portal btn-portal--outline" onClick={() => setFiliaisExtrasAlvo(null)} disabled={salvandoFiliaisExtras}>
                  Cancelar
                </button>
                <button type="button" className="btn-portal btn-portal--primary" onClick={salvarFiliaisExtras} disabled={salvandoFiliaisExtras || carregandoFiliaisExtras}>
                  {salvandoFiliaisExtras ? 'Salvando...' : 'Salvar filiais'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {alterarBase && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1060, padding: 16 }}
          onClick={() => !salvandoAlterarBase && setAlterarBase(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'var(--portal-surface)', borderRadius: 12, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.35)' }}
          >
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--portal-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong><i className="bi bi-person-vcard me-2" />Alterar — {alterarBase.nome}</strong>
              <button type="button" className="btn-close" onClick={() => setAlterarBase(null)} disabled={salvandoAlterarBase} />
            </div>

            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14, fontSize: '0.85rem' }}>
              <div style={{ color: 'var(--portal-muted)', fontSize: '0.8rem' }}>
                Estes campos vivem em <code>dbo.Usuarios</code> — a base de login e compartilhada por todos os portais Centrao.
              </div>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontWeight: 600 }}>Login <small style={{ color: 'var(--portal-muted)' }}>(3-20 caracteres, sera salvo em MAIUSCULO)</small></span>
                <input
                  type="text"
                  maxLength={20}
                  placeholder="Ex.: JOAO.SILVA"
                  value={alterarBaseForm.nome}
                  onChange={(e) => setAlterarBaseForm((f) => ({ ...f, nome: e.target.value.toUpperCase() }))}
                  style={{ padding: '8px 12px', border: '1px solid var(--portal-border)', borderRadius: 8, background: 'var(--portal-surface-soft)', color: 'var(--portal-text)', fontSize: '0.85rem' }}
                />
                <small style={{ color: 'var(--portal-muted)' }}>Alterar o login afeta o acesso do usuario em TODOS os portais Centrao.</small>
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontWeight: 600 }}>Email</span>
                <input
                  type="email"
                  value={alterarBaseForm.email}
                  onChange={(e) => setAlterarBaseForm((f) => ({ ...f, email: e.target.value }))}
                  style={{ padding: '8px 12px', border: '1px solid var(--portal-border)', borderRadius: 8, background: 'var(--portal-surface-soft)', color: 'var(--portal-text)', fontSize: '0.85rem' }}
                />
              </label>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontWeight: 600 }}>Filial</span>
                  <select
                    value={alterarBaseForm.filial}
                    onChange={(e) => setAlterarBaseForm((f) => ({ ...f, filial: e.target.value }))}
                    style={{ padding: '8px 12px', border: '1px solid var(--portal-border)', borderRadius: 8, background: 'var(--portal-surface-soft)', color: 'var(--portal-text)', fontSize: '0.85rem' }}
                  >
                    <option value="">— Nenhuma —</option>
                    {filiais.map((f) => (
                      <option key={f.codigo} value={f.codigo}>{f.codigo} · {f.nome}</option>
                    ))}
                    {alterarBaseForm.filial && !filiais.some((f) => f.codigo === alterarBaseForm.filial) && (
                      <option value={alterarBaseForm.filial}>{alterarBaseForm.filial} (fora do catalogo)</option>
                    )}
                  </select>
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontWeight: 600 }}>Grupo</span>
                  <select
                    value={alterarBaseForm.setor}
                    onChange={(e) => setAlterarBaseForm((f) => ({ ...f, setor: e.target.value }))}
                    style={{ padding: '8px 12px', border: '1px solid var(--portal-border)', borderRadius: 8, background: 'var(--portal-surface-soft)', color: 'var(--portal-text)', fontSize: '0.85rem' }}
                  >
                    <option value="">— Selecione —</option>
                    {grupos.map((g) => (
                      <option key={g.idGrupo} value={g.nome}>{g.nome}</option>
                    ))}
                    {alterarBaseForm.setor && !grupos.some((g) => g.nome === alterarBaseForm.setor) && (
                      <option value={alterarBaseForm.setor}>{alterarBaseForm.setor} (fora do catalogo)</option>
                    )}
                  </select>
                </label>
              </div>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontWeight: 600 }}>Redefinir senha <small style={{ color: 'var(--portal-muted)' }}>(4-10 caracteres, deixe vazio para nao alterar)</small></span>
                <input
                  type="password"
                  maxLength={10}
                  autoComplete="new-password"
                  placeholder="Nova senha (4 a 10 caracteres)"
                  value={alterarBaseForm.senha}
                  onChange={(e) => setAlterarBaseForm((f) => ({ ...f, senha: e.target.value }))}
                  style={{ padding: '8px 12px', border: '1px solid var(--portal-border)', borderRadius: 8, background: 'var(--portal-surface-soft)', color: 'var(--portal-text)', fontSize: '0.85rem' }}
                />
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  style={{ accentColor: 'var(--portal-blue)' }}
                  checked={alterarBaseForm.active}
                  onChange={(e) => setAlterarBaseForm((f) => ({ ...f, active: e.target.checked }))}
                />
                <span>
                  <strong>Usuario ativo</strong>
                  <br />
                  <small style={{ color: 'var(--portal-muted)' }}>Desmarcar equivale a excluir (desativa em todos os portais). Marcar reativa um usuario excluido.</small>
                </span>
              </label>
            </div>

            <div style={{ padding: '14px 20px', borderTop: '1px solid var(--portal-border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" className="btn-portal btn-portal--outline" onClick={() => setAlterarBase(null)} disabled={salvandoAlterarBase}>
                Cancelar
              </button>
              <button type="button" className="btn-portal btn-portal--primary" onClick={salvarAlterarBase} disabled={salvandoAlterarBase}>
                {salvandoAlterarBase ? 'Salvando...' : 'Salvar alteracoes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {criarOpen && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1060, padding: 16 }}
          onClick={() => !criando && setCriarOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'var(--portal-surface)', borderRadius: 12, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.35)' }}
          >
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--portal-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong><i className="bi bi-person-plus me-2" />Criar novo usuário</strong>
              <button type="button" className="btn-close" onClick={() => setCriarOpen(false)} disabled={criando} />
            </div>

            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14, fontSize: '0.85rem' }}>
              <div className="feedback-banner" style={{ background: 'rgba(245, 158, 11, 0.12)', border: '1px solid rgba(245, 158, 11, 0.35)', color: 'var(--portal-text)', padding: '10px 12px', borderRadius: 8 }}>
                <i className="bi bi-exclamation-triangle me-2" />
                <strong>Atencao:</strong> o usuario e criado em <code>dbo.Usuarios</code>, a base de login compartilhada entre TODOS os portais Centrao (financeiro, logistico, rh, ti, suc). Ele podera logar em qualquer portal onde tiver perfil vinculado.
              </div>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontWeight: 600 }}>Login <small style={{ color: 'var(--portal-muted)' }}>(3-20 caracteres, sera salvo em MAIUSCULO)</small></span>
                <input
                  type="text"
                  maxLength={20}
                  placeholder="Ex.: JOAO.SILVA"
                  value={criarForm.nome}
                  onChange={(e) => setCriarForm((f) => ({ ...f, nome: e.target.value.toUpperCase() }))}
                  style={{ padding: '8px 12px', border: '1px solid var(--portal-border)', borderRadius: 8, background: 'var(--portal-surface-soft)', color: 'var(--portal-text)', fontSize: '0.85rem' }}
                />
              </label>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontWeight: 600 }}>Senha <small style={{ color: 'var(--portal-muted)' }}>(4-10)</small></span>
                  <input
                    type="password"
                    maxLength={10}
                    autoComplete="new-password"
                    placeholder="4 a 10 caracteres"
                    value={criarForm.senha}
                    onChange={(e) => setCriarForm((f) => ({ ...f, senha: e.target.value }))}
                    style={{ padding: '8px 12px', border: '1px solid var(--portal-border)', borderRadius: 8, background: 'var(--portal-surface-soft)', color: 'var(--portal-text)', fontSize: '0.85rem' }}
                  />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontWeight: 600 }}>Confirmar</span>
                  <input
                    type="password"
                    maxLength={10}
                    autoComplete="new-password"
                    placeholder="Repita a senha"
                    value={criarForm.confirmarSenha}
                    onChange={(e) => setCriarForm((f) => ({ ...f, confirmarSenha: e.target.value }))}
                    style={{ padding: '8px 12px', border: '1px solid var(--portal-border)', borderRadius: 8, background: 'var(--portal-surface-soft)', color: 'var(--portal-text)', fontSize: '0.85rem' }}
                  />
                </label>
              </div>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontWeight: 600 }}>Email <small style={{ color: 'var(--portal-muted)' }}>(opcional)</small></span>
                <input
                  type="email"
                  placeholder="usuario@centraomateriais.com"
                  value={criarForm.email}
                  onChange={(e) => setCriarForm((f) => ({ ...f, email: e.target.value }))}
                  style={{ padding: '8px 12px', border: '1px solid var(--portal-border)', borderRadius: 8, background: 'var(--portal-surface-soft)', color: 'var(--portal-text)', fontSize: '0.85rem' }}
                />
              </label>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontWeight: 600 }}>Filial</span>
                  <select
                    value={criarForm.filial}
                    onChange={(e) => setCriarForm((f) => ({ ...f, filial: e.target.value }))}
                    style={{ padding: '8px 12px', border: '1px solid var(--portal-border)', borderRadius: 8, background: 'var(--portal-surface-soft)', color: 'var(--portal-text)', fontSize: '0.85rem' }}
                  >
                    <option value="">— Nenhuma —</option>
                    {filiais.map((f) => (
                      <option key={f.codigo} value={f.codigo}>{f.codigo} · {f.nome}</option>
                    ))}
                  </select>
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontWeight: 600 }}>Grupo</span>
                  <select
                    value={criarForm.setor}
                    onChange={(e) => setCriarForm((f) => ({ ...f, setor: e.target.value }))}
                    style={{ padding: '8px 12px', border: '1px solid var(--portal-border)', borderRadius: 8, background: 'var(--portal-surface-soft)', color: 'var(--portal-text)', fontSize: '0.85rem' }}
                  >
                    <option value="">— Selecione —</option>
                    {grupos.map((g) => (
                      <option key={g.idGrupo} value={g.nome}>{g.nome}</option>
                    ))}
                  </select>
                </label>
              </div>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontWeight: 600 }}>Perfil do Portal TI <small style={{ color: 'var(--portal-muted)' }}>(opcional — deixe em branco para so criar o login)</small></span>
                <select
                  value={criarForm.perfil_id || ''}
                  onChange={(e) => setCriarForm((f) => ({ ...f, perfil_id: e.target.value }))}
                  style={{ padding: '8px 12px', border: '1px solid var(--portal-border)', borderRadius: 8, background: 'var(--portal-surface-soft)', color: 'var(--portal-text)', fontSize: '0.85rem' }}
                >
                  <option value="">— Sem acesso ao Portal TI —</option>
                  {perfis.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
                </select>
              </label>
            </div>

            <div style={{ padding: '14px 20px', borderTop: '1px solid var(--portal-border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" className="btn-portal btn-portal--outline" onClick={() => setCriarOpen(false)} disabled={criando}>
                Cancelar
              </button>
              <button type="button" className="btn-portal btn-portal--primary" onClick={criarUsuarioBase} disabled={criando}>
                {criando ? 'Criando...' : 'Criar usuário'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editUser && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050, padding: 16 }}
          onClick={() => !salvandoEdicao && setEditUser(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'var(--portal-surface)', borderRadius: 12, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.35)' }}
          >
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--portal-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong><i className="bi bi-person-gear me-2" />Alterar — {editUser.nome}</strong>
              <button type="button" className="btn-close" onClick={() => setEditUser(null)} disabled={salvandoEdicao} />
            </div>

            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--portal-muted)' }}>
                Filial {editUser.filial || '-'} · {editUser.email || 'sem email'}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: '0.85rem' }}>
                <span style={{ fontWeight: 600 }}>Perfil</span>
                <select
                  value={form.perfil_id || ''}
                  onChange={(e) => setForm((f) => ({ ...f, perfil_id: e.target.value ? Number(e.target.value) : '' }))}
                  style={{ width: '100%', minHeight: 40, padding: '8px 12px', border: '1px solid var(--portal-border)', borderRadius: 8, background: 'var(--portal-surface-soft)', color: 'var(--portal-text)', outline: 'none', fontSize: '0.85rem' }}
                >
                  <option value="">Selecione um perfil...</option>
                  {perfis.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
                </select>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: '0.85rem' }}>
                <button
                  type="button"
                  onClick={() => setEditarBaseOpen((o) => !o)}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, width: '100%', minHeight: 40, padding: '8px 12px', border: '1px solid var(--portal-border)', borderRadius: 8, background: 'var(--portal-surface-soft)', color: 'var(--portal-text)', cursor: 'pointer', fontSize: '0.85rem' }}
                >
                  <span><i className="bi bi-person-vcard me-2" /><strong>Dados base</strong> (login, email, filial, setor, senha)</span>
                  <i className={`bi bi-chevron-${editarBaseOpen ? 'up' : 'down'}`} />
                </button>
                {editarBaseOpen && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, border: '1px solid var(--portal-border)', borderRadius: 8, padding: 12, background: 'var(--portal-surface-soft)' }}>
                    <small style={{ color: 'var(--portal-muted)' }}>
                      Estes campos vivem em <code>dbo.Usuarios</code> — a base de login e compartilhada por todos os portais Centrao.
                    </small>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <span>Login <small style={{ color: 'var(--portal-muted)' }}>(3-20 caracteres, MAIUSCULO)</small></span>
                      <input
                        type="text"
                        maxLength={20}
                        placeholder="Ex.: JOAO.SILVA"
                        value={editBaseForm.nome}
                        onChange={(e) => setEditBaseForm((f) => ({ ...f, nome: e.target.value.toUpperCase() }))}
                        style={{ padding: '8px 12px', border: '1px solid var(--portal-border)', borderRadius: 8, background: 'var(--portal-surface)', color: 'var(--portal-text)', fontSize: '0.85rem' }}
                      />
                    </label>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <span>Email</span>
                      <input
                        type="email"
                        value={editBaseForm.email}
                        onChange={(e) => setEditBaseForm((f) => ({ ...f, email: e.target.value }))}
                        style={{ padding: '8px 12px', border: '1px solid var(--portal-border)', borderRadius: 8, background: 'var(--portal-surface)', color: 'var(--portal-text)', fontSize: '0.85rem' }}
                      />
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <span>Filial</span>
                        <select
                          value={editBaseForm.filial}
                          onChange={(e) => setEditBaseForm((f) => ({ ...f, filial: e.target.value }))}
                          style={{ padding: '8px 12px', border: '1px solid var(--portal-border)', borderRadius: 8, background: 'var(--portal-surface)', color: 'var(--portal-text)', fontSize: '0.85rem' }}
                        >
                          <option value="">— Nenhuma —</option>
                          {filiais.map((f) => (
                            <option key={f.codigo} value={f.codigo}>{f.codigo} · {f.nome}</option>
                          ))}
                          {editBaseForm.filial && !filiais.some((f) => f.codigo === editBaseForm.filial) && (
                            <option value={editBaseForm.filial}>{editBaseForm.filial} (fora do catalogo)</option>
                          )}
                        </select>
                      </label>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <span>Grupo</span>
                        <select
                          value={editBaseForm.setor}
                          onChange={(e) => setEditBaseForm((f) => ({ ...f, setor: e.target.value }))}
                          style={{ padding: '8px 12px', border: '1px solid var(--portal-border)', borderRadius: 8, background: 'var(--portal-surface)', color: 'var(--portal-text)', fontSize: '0.85rem' }}
                        >
                          <option value="">— Selecione —</option>
                          {grupos.map((g) => (
                            <option key={g.idGrupo} value={g.nome}>{g.nome}</option>
                          ))}
                          {editBaseForm.setor && !grupos.some((g) => g.nome === editBaseForm.setor) && (
                            <option value={editBaseForm.setor}>{editBaseForm.setor} (fora do catalogo)</option>
                          )}
                        </select>
                      </label>
                    </div>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <span>Redefinir senha <small style={{ color: 'var(--portal-muted)' }}>(4-10 caracteres, deixe vazio para nao alterar)</small></span>
                      <input
                        type="password"
                        maxLength={10}
                        autoComplete="new-password"
                        placeholder="Nova senha (4 a 10 caracteres)"
                        value={editBaseForm.senha}
                        onChange={(e) => setEditBaseForm((f) => ({ ...f, senha: e.target.value }))}
                        style={{ padding: '8px 12px', border: '1px solid var(--portal-border)', borderRadius: 8, background: 'var(--portal-surface)', color: 'var(--portal-text)', fontSize: '0.85rem' }}
                      />
                    </label>
                    <button
                      type="button"
                      className="btn-portal btn-portal--outline"
                      onClick={salvarDadosBase}
                      disabled={salvandoEdicao}
                      style={{ alignSelf: 'flex-start' }}
                    >
                      {salvandoEdicao ? 'Salvando...' : 'Salvar dados base'}
                    </button>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: '0.85rem' }}>
                <span style={{ fontWeight: 600 }}>Canal padrão</span>
                <select
                  value={form.canal_id || ''}
                  onChange={(e) => mudarPadrao(e.target.value)}
                  style={{ width: '100%', minHeight: 40, padding: '8px 12px', border: '1px solid var(--portal-border)', borderRadius: 8, background: 'var(--portal-surface-soft)', color: 'var(--portal-text)', outline: 'none', fontSize: '0.85rem' }}
                >
                  <option value="">— Sem canal —</option>
                  {canais.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </select>
                <small style={{ color: 'var(--portal-muted)' }}>
                  Onde ele atende por padrão e de onde saem as conversas que ele inicia.
                </small>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: '0.85rem' }}>
                <span style={{ fontWeight: 600 }}>Canais extras (também pode ver)</span>
                {!form.canal_id ? (
                  <small style={{ color: 'var(--portal-muted)' }}>Defina um canal padrão para liberar canais extras.</small>
                ) : canais.filter((c) => c.id !== Number(form.canal_id)).length === 0 ? (
                  <small style={{ color: 'var(--portal-muted)' }}>Não há outros canais.</small>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => setExtrasOpen((o) => !o)}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, width: '100%', minHeight: 40, padding: '8px 12px', border: '1px solid var(--portal-border)', borderRadius: 8, background: 'var(--portal-surface-soft)', color: 'var(--portal-text)', cursor: 'pointer', fontSize: '0.85rem' }}
                    >
                      <span>{form.extras.length ? `${form.extras.length} canal(is) selecionado(s)` : 'Selecionar canais...'}</span>
                      <i className={`bi bi-chevron-${extrasOpen ? 'up' : 'down'}`} />
                    </button>
                    {extrasOpen && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 180, overflowY: 'auto', border: '1px solid var(--portal-border)', borderRadius: 8, padding: 10, background: 'var(--portal-surface-soft)' }}>
                        {canais.filter((c) => c.id !== Number(form.canal_id)).map((c) => (
                          <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              style={{ accentColor: 'var(--portal-blue)' }}
                              checked={form.extras.includes(c.id)}
                              onChange={() => toggleExtra(c.id)}
                            />
                            <i className="bi bi-whatsapp" style={{ color: '#25D366', fontSize: '0.8rem' }} />
                            {c.nome}
                          </label>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', opacity: form.extras.length === 0 ? 0.5 : 1, cursor: form.extras.length === 0 ? 'not-allowed' : 'pointer' }}>
                <input
                  type="checkbox"
                  style={{ accentColor: '#2A8A9C' }}
                  disabled={form.extras.length === 0}
                  checked={form.assumir}
                  onChange={(e) => setForm((f) => ({ ...f, assumir: e.target.checked }))}
                />
                <span>
                  <strong>Assumir/responder</strong> atendimentos dos canais extras
                  <br />
                  <small style={{ color: 'var(--portal-muted)' }}>Desligado = só visualiza os extras; ligado = também atua neles.</small>
                </span>
              </label>
            </div>

            <div style={{ padding: '14px 20px', borderTop: '1px solid var(--portal-border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" className="btn-portal btn-portal--outline" onClick={() => setEditUser(null)} disabled={salvandoEdicao}>
                Cancelar
              </button>
              <button type="button" className="btn-portal btn-portal--primary" onClick={salvarEdicao} disabled={salvandoEdicao}>
                {salvandoEdicao ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
